import { API_URLS } from '@chessx/shared/api-routes'
import { API_CONTRACT_VERSION, API_CONTRACT_VERSION_HEADER } from '@chessx/shared/api-contract'
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import useUserStore from '../store/UserStore'

type TokenCallback = (token: string | null, err: boolean) => void
type UpdateTokenCallback = (token: null, err: boolean) => void

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_ENDPOINT,
  headers: {
    Accept: 'application/json',
    [API_CONTRACT_VERSION_HEADER]: String(API_CONTRACT_VERSION)
  }
})

let isTokenRefreshing = false
let requestCaching: TokenCallback[] = []

axiosInstance.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const token = useUserStore.getState().token
    if (token) {
      const headers = config.headers as { common?: Record<string, string> }
      headers.common = { Authorization: token }
    }
    return config
  },
  (error: AxiosError) => {
    Promise.reject(error.response || error.message)
  }
)

axiosInstance.interceptors.response.use(
  function (response: AxiosResponse) {
    return response
  },
  function (error: AxiosError<{ expired?: boolean }>) {
    const status = error?.response?.status || 0
    const resBaseURL = error?.response?.config?.baseURL
    if (resBaseURL === import.meta.env.VITE_BACKEND_ENDPOINT && status === 401) {
      const token = useUserStore.getState().token
      const refreshToken = useUserStore.getState().refreshToken
      if (token && refreshToken && error?.response?.data?.expired) {
        if (!isTokenRefreshing) {
          isTokenRefreshing = true
          updateToken(refreshToken, (_, err) => {
            if (err) {
              localStorage.clear()
            }
            setTimeout(() => {
              recallCachedAPIs(err)
              requestCaching = []
              isTokenRefreshing = false
            }, 1000)
          })
        }

        return new Promise((resolve, reject) => {
          subscribeToken((newToken, err) => {
            if (err) return reject(error)
            if (error.config) {
              const headers = error.config.headers as Record<string, string>
              headers.Authorization = newToken as string
              resolve(axios(error.config))
            }
          })
        })
      }
      localStorage.clear()
      window.location.assign('/')
    }
    return Promise.reject(error)
  }
)
const subscribeToken = (cb: TokenCallback): void => {
  requestCaching.push(cb)
}

const recallCachedAPIs = (error: boolean): void => {
  const token = useUserStore.getState().token
  requestCaching.forEach(cb => cb(token, error))
}

const updateToken = async (refreshToken: string, callback: UpdateTokenCallback): Promise<void> => {
  try {
    const response = await axiosInstance.post(API_URLS.REFRESH_TOKEN, { refreshToken })
    const { data } = response.data
    if (data?.token && data?.refreshToken) {
      useUserStore.getState().setToken(data?.token)
      useUserStore.getState().setRefreshToken(data?.refreshToken)
      callback(null, false)
      return
    }
    callback(null, true)
  } catch (err) {
    callback(null, true)
  }
}

export default axiosInstance
